/**
 * Hybrid Communication Manager for Phase 1 P2P Integration
 * 
 * This manager combines WebSocket and P2P communication to provide:
 * - Primary WebSocket connection for reliability
 * - P2P connections for direct peer communication
 * - Automatic fallback mechanisms
 * - Consensus validation between server and peers
 * 
 * Phase 1 Implementation: Server remains source of truth while P2P is established
 */

import { P2PStateManager } from './P2PStateManager';
import { P2PSignalingClient, P2PReconnectionManager } from './P2PSignalingClient';
import P2PGameEngine from './P2PGameEngine';
import ByzantineConsensus from './ByzantineConsensus';
import SecureHandComparison from './SecureHandComparison';
import { DistributedGameState } from './DistributedGameState';

interface HybridMessage {
  type: string;
  data: any;
  source: 'websocket' | 'p2p';
  timestamp: number;
  messageId: string;
}

interface ConnectionStatus {
  websocket: 'connected' | 'connecting' | 'disconnected' | 'error';
  p2p: 'connected' | 'connecting' | 'disconnected' | 'error';
  signaling: 'connected' | 'connecting' | 'disconnected' | 'error';
  totalPeers: number;
  connectedPeers: number;
  fallbackMode: boolean;
}

/**
 * Hybrid Communication Manager
 * Orchestrates WebSocket + P2P communication for Phase 1
 */
export class HybridCommunicationManager {
  private static instance: HybridCommunicationManager;
  
  // Communication channels
  private websocket: WebSocket | null = null;
  private p2pStateManager: P2PStateManager;
  private p2pSignaling: P2PSignalingClient;
  private p2pReconnection: P2PReconnectionManager;
  
  // Phase 2: P2P Game Engine components
  private p2pGameEngine: P2PGameEngine;
  private byzantineConsensus: ByzantineConsensus;
  private secureHandComparison: SecureHandComparison;
  private distributedGameState: DistributedGameState;
  private p2pGameMode: boolean = false; // Enable full P2P gameplay
  
  // State management
  private messageHandlers = new Map<string, Function[]>();
  private connectionStatus: ConnectionStatus;
  private roomId: string = '';
  private playerId: string = '';
  private fallbackMode: boolean = false;
  private p2pEnabled: boolean = true;
  
  // Message consensus tracking
  private messageConsensus = new Map<string, {
    websocket?: any;
    p2p?: any;
    timestamp: number;
  }>();

  private constructor() {
    this.p2pStateManager = P2PStateManager.getInstance();
    this.p2pSignaling = P2PSignalingClient.getInstance();
    this.p2pReconnection = P2PReconnectionManager.getInstance();
    
    // Initialize Phase 2 P2P components
    this.p2pGameEngine = P2PGameEngine.getInstance();
    this.byzantineConsensus = ByzantineConsensus.getInstance();
    this.secureHandComparison = SecureHandComparison.getInstance();
    this.distributedGameState = DistributedGameState.getInstance();
    
    this.connectionStatus = {
      websocket: 'disconnected',
      p2p: 'disconnected', 
      signaling: 'disconnected',
      totalPeers: 0,
      connectedPeers: 0,
      fallbackMode: false
    };
    
    this.setupP2PIntegration();
    this.setupP2PGameEngine();
  }

  static getInstance(): HybridCommunicationManager {
    if (!HybridCommunicationManager.instance) {
      HybridCommunicationManager.instance = new HybridCommunicationManager();
    }
    return HybridCommunicationManager.instance;
  }

  /**
   * Connect to both WebSocket and P2P systems
   */
  async connect(serverUrl: string, roomId: string, playerName: string): Promise<void> {
    this.roomId = roomId;
    this.playerId = this.generatePlayerId(playerName, roomId);
    
    console.log('🔄 Starting hybrid connection to room:', roomId);
    
    // 1. Connect to WebSocket (primary, reliable)
    await this.connectWebSocket(serverUrl);
    
    // 2. Connect to P2P signaling (for WebRTC coordination)  
    if (this.p2pEnabled) {
      try {
        await this.connectP2PSignaling(serverUrl, roomId, this.playerId);
      } catch (error) {
        console.warn('P2P signaling failed, continuing with WebSocket only:', error);
        this.fallbackMode = true;
      }
    }
    
    this.updateConnectionStatus();
    console.log('✅ Hybrid connection established');
  }

  /**
   * Connect to WebSocket server (existing reliable connection)
   */
  private async connectWebSocket(serverUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.connectionStatus.websocket = 'connecting';
      
      try {
        // Add roomId parameter for server routing
        const wsUrl = this.roomId 
          ? `${serverUrl}?roomId=${encodeURIComponent(this.roomId)}`
          : serverUrl;
        
        console.log('🔗 Attempting WebSocket connection to:', wsUrl);
        this.websocket = new WebSocket(wsUrl);
        
        this.websocket.onopen = () => {
          console.log('🔌 WebSocket connected successfully to:', wsUrl);
          this.connectionStatus.websocket = 'connected';
          resolve();
        };
        
        this.websocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleWebSocketMessage(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };
        
        this.websocket.onerror = (error) => {
          console.error('WebSocket connection failed:', {
            url: wsUrl,
            error: error,
            readyState: this.websocket?.readyState,
            timestamp: new Date().toISOString()
          });
          this.connectionStatus.websocket = 'error';
          reject(new Error(`WebSocket connection failed to ${wsUrl}`));
        };
        
        this.websocket.onclose = () => {
          console.log('WebSocket disconnected');
          this.connectionStatus.websocket = 'disconnected';
          this.handleWebSocketDisconnection();
        };
        
        // Timeout after 5 seconds
        setTimeout(() => {
          if (this.websocket?.readyState !== WebSocket.OPEN) {
            reject(new Error('WebSocket connection timeout'));
          }
        }, 5000);
        
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Connect to P2P signaling for WebRTC coordination
   */
  private async connectP2PSignaling(serverUrl: string, roomId: string, playerId: string): Promise<void> {
    this.connectionStatus.signaling = 'connecting';
    
    try {
      // Instead of creating a new WebSocket, we'll use the existing one
      // Send P2P room join message through the main WebSocket
      this.connectionStatus.signaling = 'connected';
      
      // Send P2P room join after WebSocket is connected
      await this.joinP2PRoom(roomId, playerId);
      
      console.log('🎯 P2P signaling ready (using main WebSocket)');
      
      // Setup P2P reconnection manager
      this.p2pReconnection.setStateManager(this.p2pStateManager);
      
    } catch (error) {
      this.connectionStatus.signaling = 'error';
      throw error;
    }
  }

  /**
   * Setup P2P integration with state manager callbacks
   */
  private setupP2PIntegration(): void {
    // Handle P2P state restoration
    this.p2pStateManager.setStateRestoredHandler((gameState) => {
      console.log('📥 Game state restored from P2P peer');
      this.emit('game-state-restored', { source: 'p2p', gameState });
    });

    // Handle P2P state updates
    this.p2pStateManager.setStateUpdatedHandler((gameState) => {
      console.log('🔄 Game state updated via P2P consensus');
      this.emit('game-state-updated', { source: 'p2p', gameState });
    });

    // Setup P2P signaling event handlers
    this.p2pSignaling.onPeerJoined = (peerId: string) => {
      console.log(`👋 Peer joined: ${peerId}`);
      this.handlePeerJoined(peerId);
    };

    this.p2pSignaling.onPeerLeft = (peerId: string) => {
      console.log(`👋 Peer left: ${peerId}`);
      this.handlePeerLeft(peerId);
    };
  }

  /**
   * Send message through both WebSocket and P2P channels
   */
  send(type: string, data: any): void {
    // Clear stored player ID when explicitly leaving (not just disconnecting)
    if (type === 'leave-room') {
      const playerName = this.playerId ? this.playerId.split('_')[0] : 'unknown';
      this.clearStoredPlayerId(playerName);
    }
    
    const message = {
      type,
      data,
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
      playerId: this.playerId
    };

    // Always send via WebSocket (primary channel)
    if (this.websocket?.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify({ type, data }));
      console.log(`📤 Sent via WebSocket: ${type}`);
    }

    // Also send via P2P if available (for consensus validation)
    if (this.p2pEnabled && !this.fallbackMode) {
      const p2pStatus = this.p2pStateManager.getP2PStatus();
      if (p2pStatus.connectedPeers > 0) {
        this.p2pStateManager.broadcastStateUpdate(message.messageId, {
          type,
          data,
          source: 'local',
          timestamp: message.timestamp
        });
        console.log(`🌐 Sent via P2P: ${type} to ${p2pStatus.connectedPeers} peers`);
      }
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleWebSocketMessage(message: any): void {
    console.log(`📨 Received WebSocket message: ${message.type}`, message);
    
    // Check if this is a P2P signaling message
    if (this.isP2PSignalingMessage(message.type)) {
      this.handleP2PSignalingMessage(message);
      return;
    }

    const hybridMessage: HybridMessage = {
      ...message,
      source: 'websocket',
      timestamp: Date.now(),
      messageId: this.generateMessageId()
    };

    // Process message directly since P2P consensus not ready yet in Phase 1
    console.log(`🔄 Processing message: ${message.type}`);
    this.processMessage(hybridMessage);
  }
  
  /**
   * Check if message is P2P signaling related
   */
  private isP2PSignalingMessage(messageType: string): boolean {
    const p2pTypes = [
      'joined_p2p_room',
      'peer_joined',
      'peer_left',
      'webrtc_offer',
      'webrtc_answer',
      'webrtc_ice_candidate',
      'room_peers'
    ];
    return p2pTypes.includes(messageType);
  }
  
  /**
   * Handle P2P signaling messages
   */
  private handleP2PSignalingMessage(message: any): void {
    console.log('📡 P2P signaling message:', message.type);
    
    switch (message.type) {
      case 'joined_p2p_room':
        console.log('✅ Successfully joined P2P room:', message.data);
        const { currentPeers } = message.data;
        this.connectionStatus.totalPeers = currentPeers.length;
        this.updateConnectionStatus();
        break;
        
      case 'peer_joined':
        console.log('👋 Peer joined:', message.data.peerId);
        this.handlePeerJoined(message.data.peerId);
        break;
        
      case 'peer_left':
        console.log('👋 Peer left:', message.data.peerId);
        this.handlePeerLeft(message.data.peerId);
        break;
        
      case 'webrtc_offer':
      case 'webrtc_answer':
      case 'webrtc_ice_candidate':
        // Forward to P2P state manager for WebRTC handling
        if (this.p2pStateManager) {
          this.p2pStateManager.handleSignalingMessage(message);
        }
        break;
    }
  }

  /**
   * Validate message consensus between WebSocket and P2P
   */
  private validateMessageConsensus(message: HybridMessage): void {
    const consensusKey = `${message.type}_${message.timestamp}`;
    
    if (!this.messageConsensus.has(consensusKey)) {
      this.messageConsensus.set(consensusKey, { timestamp: Date.now() });
    }
    
    const consensus = this.messageConsensus.get(consensusKey)!;
    
    if (message.source === 'websocket') {
      consensus.websocket = message;
    } else {
      consensus.p2p = message;
    }

    // If we have both sources or only WebSocket (authoritative), process
    if (consensus.websocket && (consensus.p2p || this.fallbackMode)) {
      this.processMessage(consensus.websocket);
      this.messageConsensus.delete(consensusKey);
    }
    
    // Cleanup old consensus entries
    setTimeout(() => {
      this.messageConsensus.delete(consensusKey);
    }, 5000);
  }

  /**
   * Process validated message and emit to handlers
   */
  private processMessage(message: HybridMessage): void {
    const handlers = this.messageHandlers.get(message.type);
    console.log(`🎯 Looking for handlers for ${message.type}:`, handlers ? handlers.length : 'none found');
    
    if (handlers) {
      handlers.forEach(handler => {
        try {
          console.log(`📞 Calling handler for ${message.type}`);
          handler(message);
        } catch (error) {
          console.error(`Error in message handler for ${message.type}:`, error);
        }
      });
    } else {
      console.warn(`⚠️ No handlers registered for message type: ${message.type}`);
    }
  }

  /**
   * Handle peer joining (setup P2P connection)
   */
  private async handlePeerJoined(peerId: string): Promise<void> {
    try {
      this.connectionStatus.totalPeers += 1;
      
      // Initiate P2P connection to new peer
      await this.p2pStateManager.connectToPeer(peerId, true);
      
      this.updateConnectionStatus();
      this.emit('peer-joined', { peerId });
      
    } catch (error) {
      console.error(`Failed to connect to peer ${peerId}:`, error);
    }
  }

  /**
   * Handle peer leaving
   */
  private handlePeerLeft(peerId: string): void {
    this.connectionStatus.totalPeers = Math.max(0, this.connectionStatus.totalPeers - 1);
    this.updateConnectionStatus();
    this.emit('peer-left', { peerId });
  }

  /**
   * Handle WebSocket disconnection
   */
  private handleWebSocketDisconnection(): void {
    console.log('🔌 WebSocket disconnected, checking P2P status...');
    
    const p2pStatus = this.p2pStateManager.getP2PStatus();
    
    if (p2pStatus.connectedPeers > 0 && this.p2pEnabled) {
      console.log('🌐 Maintaining connection via P2P peers');
      this.fallbackMode = true;
    } else {
      console.log('❌ Complete disconnection, attempting reconnect...');
      this.attemptReconnection();
    }
    
    this.updateConnectionStatus();
  }

  /**
   * Attempt to reconnect both WebSocket and P2P
   */
  private async attemptReconnection(): Promise<void> {
    if (!this.roomId || !this.playerId) return;
    
    try {
      console.log('🔄 Attempting hybrid reconnection...');
      
      // Try P2P reconnection first (faster)
      if (this.p2pEnabled) {
        const p2pSuccess = await this.p2pReconnection.attemptP2PReconnection(
          this.roomId,
          this.playerId,
          'wss://poker-server.antoine-delorme.workers.dev'
        );
        
        if (p2pSuccess) {
          console.log('✅ P2P reconnection successful');
          this.connectionStatus.p2p = 'connected';
        }
      }
      
      // Then try WebSocket reconnection
      if (this.websocket?.readyState !== WebSocket.OPEN) {
        await this.connectWebSocket('wss://poker-server.antoine-delorme.workers.dev');
      }
      
      this.fallbackMode = false;
      this.updateConnectionStatus();
      
    } catch (error) {
      console.error('Reconnection failed:', error);
      setTimeout(() => this.attemptReconnection(), 5000);
    }
  }

  /**
   * Update connection status and notify listeners
   */
  private updateConnectionStatus(): void {
    const p2pStatus = this.p2pStateManager.getP2PStatus();
    
    this.connectionStatus.p2p = p2pStatus.connectedPeers > 0 ? 'connected' : 'disconnected';
    this.connectionStatus.connectedPeers = p2pStatus.connectedPeers;
    this.connectionStatus.fallbackMode = this.fallbackMode;
    
    this.emit('connection-status-changed', this.connectionStatus);
  }

  /**
   * Register message handler
   */
  on(type: string, handler: Function): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)!.push(handler);
  }

  /**
   * Remove message handler
   */
  off(type: string, handler: Function): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to handlers
   */
  private emit(type: string, data: any): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler({ type, data });
        } catch (error) {
          console.error(`Error in event handler for ${type}:`, error);
        }
      });
    }
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return { ...this.connectionStatus };
  }

  /**
   * Enable/disable P2P functionality
   */
  setP2PEnabled(enabled: boolean): void {
    this.p2pEnabled = enabled;
    if (!enabled) {
      this.fallbackMode = true;
      this.connectionStatus.p2p = 'disconnected';
      this.connectionStatus.signaling = 'disconnected';
    }
    this.updateConnectionStatus();
  }

  /**
   * Clear stored player ID (for explicit leave, not reconnection)
   */
  private clearStoredPlayerId(playerName: string, roomId?: string): void {
    const roomKey = roomId || this.roomId || 'unknown';
    const storageKey = `poker_player_id_${playerName}_${roomKey}`;
    try {
      localStorage.removeItem(storageKey);
      console.log('🗑️ CLEARED STORED PLAYER ID on leave:', this.playerId);
    } catch (error) {
      console.warn('Failed to clear stored player ID:', error);
    }
  }

  /**
   * Disconnect from all channels
   */
  disconnect(): void {
    console.log('🔌 Disconnecting hybrid communication...');
    
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    
    if (this.p2pEnabled) {
      this.p2pSignaling.disconnect();
      this.p2pStateManager.disconnect();
      this.p2pReconnection.disconnect();
    }
    
    this.messageHandlers.clear();
    this.messageConsensus.clear();
    
    this.connectionStatus = {
      websocket: 'disconnected',
      p2p: 'disconnected',
      signaling: 'disconnected', 
      totalPeers: 0,
      connectedPeers: 0,
      fallbackMode: false
    };
  }

  /**
   * Generate or retrieve persistent player ID for reconnections
   */
  private generatePlayerId(playerName: string, roomId?: string): string {
    // Create a storage key based on player name and room
    const roomKey = roomId || this.roomId || 'unknown';
    const storageKey = `poker_player_id_${playerName}_${roomKey}`;
    
    // Try to get existing player ID from localStorage
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const { playerId, timestamp } = JSON.parse(stored);
        // Use stored ID if it's less than 30 minutes old (same session)
        if (Date.now() - timestamp < 30 * 60 * 1000) {
          console.log('🔄 REUSING EXISTING PLAYER ID for reconnection:', playerId);
          return playerId;
        }
      }
    } catch (error) {
      console.warn('Failed to read stored player ID:', error);
    }
    
    // Generate new player ID
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    const newPlayerId = `${playerName}_${timestamp}_${random}`;
    
    // Store the new player ID for future reconnections
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        playerId: newPlayerId,
        timestamp: Date.now()
      }));
      console.log('💾 STORED NEW PLAYER ID for future reconnections:', newPlayerId);
    } catch (error) {
      console.warn('Failed to store player ID:', error);
    }
    
    return newPlayerId;
  }

  /**
   * Join P2P room for direct peer connections
   */
  private async joinP2PRoom(roomId: string, playerId: string): Promise<void> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not ready for P2P room join');
      return;
    }
    
    try {
      // Send join P2P room message through the main WebSocket
      this.websocket.send(JSON.stringify({
        type: 'join_p2p_room',
        data: {
          roomId: roomId,
          playerId: playerId
        }
      }));
      
      console.log(`🌐 Joining P2P room: ${roomId} as ${playerId}`);
    } catch (error) {
      console.warn('Failed to join P2P room:', error);
      this.fallbackMode = true;
    }
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get P2P statistics for debugging
   */
  getP2PStats(): any {
    if (!this.p2pEnabled) {
      return { enabled: false };
    }
    
    const p2pStatus = this.p2pStateManager.getP2PStatus();
    const connectionStatus = this.p2pReconnection.getConnectionStatus();
    
    return {
      enabled: true,
      stateManager: p2pStatus,
      connections: connectionStatus,
      fallbackMode: this.fallbackMode,
      messageConsensusQueue: this.messageConsensus.size
    };
  }

  // =========================
  // PHASE 2: P2P GAME ENGINE
  // =========================

  /**
   * Setup Distributed Game State handlers
   */
  private setupDistributedGameStateHandlers(): void {
    // Handle distributed game state updates
    this.distributedGameState.onStateUpdate((gameState) => {
      console.log('🔄 Distributed game state updated:', gameState.round);
      
      // Convert distributed state to client format and emit
      const clientGameState = this.convertDistributedToClientState(gameState);
      this.emit('p2p-game-updated', clientGameState);
    });
    
    // Handle distributed actions
    this.distributedGameState.onActionProcessed((action) => {
      console.log('✅ Distributed action processed:', action.actionType);
      this.emit('p2p-action-processed', action);
    });
    
    // Handle distributed game state errors
    this.distributedGameState.onError((error) => {
      console.error('❌ Distributed game state error:', error);
      this.emit('p2p-game-error', { error });
      
      // Consider fallback to server mode
      this.fallbackToServerMode('Distributed state error: ' + error);
    });
  }

  /**
   * Setup P2P Game Engine integration
   */
  private setupP2PGameEngine(): void {
    // Setup P2P game engine event handlers
    this.p2pGameEngine.setGameStateUpdateHandler((gameState) => {
      console.log('🎮 P2P game state updated:', gameState.round);
      this.emit('p2p-game-updated', gameState);
    });

    this.p2pGameEngine.setConsensusReachedHandler((actionId, action) => {
      console.log('✅ P2P consensus reached for action:', action.actionType);
      this.emit('p2p-consensus-reached', { actionId, action });
    });

    this.p2pGameEngine.setMentalPokerErrorHandler((error) => {
      console.error('❌ Mental Poker error:', error);
      this.emit('mental-poker-error', { error });
      
      // Fallback to server mode on Mental Poker errors
      this.fallbackToServerMode('Mental Poker error: ' + error);
    });

    // Setup Byzantine consensus handlers
    this.byzantineConsensus.setConsensusReachedHandler((sequenceNumber, payload) => {
      console.log('🛡️ Byzantine consensus reached:', sequenceNumber);
      this.handleByzantineConsensus(payload);
    });

    this.byzantineConsensus.setByzantineDetectedHandler((suspiciousNode, evidence) => {
      console.warn('🚨 Byzantine fault detected from:', suspiciousNode);
      this.emit('byzantine-detected', { suspiciousNode, evidence });
    });

    // Setup secure hand comparison handlers
    this.secureHandComparison.setHandComparisonCompleteHandler((winner, revealedHands) => {
      console.log('🏆 Secure hand comparison complete. Winner:', winner);
      this.emit('showdown-complete', { winner, revealedHands });
    });
  }

  /**
   * Enable P2P game mode with Distributed Game State
   */
  async enableP2PGameMode(playerIds: string[]): Promise<void> {
    if (playerIds.length < 2) {
      throw new Error('P2P game mode requires at least 2 players');
    }

    console.log('🚀 Enabling P2P game mode with Distributed Game State...');

    try {
      // Initialize Byzantine consensus for distributed game state
      await this.byzantineConsensus.initialize(this.playerId, playerIds);
      console.log('🛡️ Byzantine consensus initialized');
      
      // Initialize distributed game state handlers
      this.setupDistributedGameStateHandlers();
      
      this.p2pGameMode = true;
      console.log('✅ P2P game mode enabled with distributed state');
      
      this.emit('p2p-mode-enabled', { playerIds });

    } catch (error) {
      console.error('❌ Failed to enable P2P game mode:', error);
      this.fallbackToServerMode('Failed to initialize P2P: ' + error);
    }
  }

  /**
   * Start new P2P game with Distributed Game State
   */
  async startP2PGame(connectedPlayers?: any[]): Promise<void> {
    if (!this.p2pGameMode) {
      throw new Error('P2P game mode not enabled');
    }

    console.log('🎯 Starting P2P game with Distributed Game State...');

    try {
      // Use provided players or fall back to empty array
      const currentPlayers = connectedPlayers || [];
      
      // Filter for connected players only
      const connectedPlayersList = currentPlayers.filter(player => 
        player.isConnected !== false && (player.chips || 0) > 0
      );
      
      console.log(`🎮 Starting P2P game with ${connectedPlayersList.length} connected players`);
      
      // Check if we have enough players for any game (P2P or server)
      if (connectedPlayersList.length < 2) {
        console.warn('⚠️ Not enough connected players for any game (need at least 2)');
        throw new Error('Need at least 2 connected players to start a game');
      }
      
      console.log(`✅ P2P game viable with ${connectedPlayersList.length} connected players`);
      
      // Convert current players to distributed format
      const distributedPlayers = connectedPlayersList.map(player => ({
        id: player.id,
        name: player.name,
        chips: player.chips || 1000,
        currentBet: 0,
        holeCards: [],
        hasActed: false,
        isFolded: false,
        isConnected: true,
        isActive: true,
        lastActionTime: Date.now(),
        totalBuyIns: player.totalBuyIns || 1000,
        rebuyCount: player.rebuyCount || 0,
        netScore: player.netScore || 0
      }));

      // Initialize distributed game state
      await this.distributedGameState.initializeGame(
        `game_${Date.now()}`,
        this.roomId,
        this.playerId,
        distributedPlayers,
        { smallBlind: 10, bigBlind: 20 }
      );
      
      console.log('✅ P2P game started with distributed state');
      
    } catch (error) {
      console.error('❌ Failed to start P2P game:', error);
      this.fallbackToServerMode('Failed to start P2P game: ' + error);
    }
  }

  /**
   * Process P2P player action with Distributed Consensus
   */
  async processP2PAction(actionType: string, amount?: number): Promise<void> {
    if (!this.p2pGameMode) {
      // Fallback to WebSocket action
      this.send('poker-action', { action: actionType, amount });
      return;
    }

    console.log(`🎯 Processing P2P action via distributed consensus: ${actionType}`);

    try {
      // Process action through distributed game state
      const success = await this.distributedGameState.processPlayerAction(
        this.playerId,
        actionType as any,
        amount
      );
      
      if (success) {
        console.log(`✅ P2P action processed via consensus: ${actionType}`);
      } else {
        console.warn(`❌ P2P action rejected by consensus: ${actionType}`);
        // Fallback to server for this action
        this.send('poker-action', { action: actionType, amount });
      }
      
    } catch (error) {
      console.error('❌ Failed to process P2P action:', error);
      this.fallbackToServerMode('P2P action failed: ' + error);
    }
  }

  /**
   * Handle Byzantine consensus results
   */
  private handleByzantineConsensus(payload: any): void {
    switch (payload.type) {
      case 'hand_comparison_result':
        this.emit('consensus-showdown-result', payload.data);
        break;
      case 'fraud_detection':
        this.emit('fraud-detected', payload.data);
        break;
      default:
        console.log('🛡️ Byzantine consensus result:', payload);
    }
  }

  /**
   * Fallback to server mode when P2P fails
   */
  private fallbackToServerMode(reason: string): void {
    console.warn('🔄 Falling back to server mode:', reason);
    
    this.p2pGameMode = false;
    this.fallbackMode = true;
    
    this.emit('p2p-fallback', { reason });
    this.updateConnectionStatus();
    
    // Only auto-start server game for technical P2P failures, not player count issues
    if (reason.includes('P2P game') && !reason.includes('connected players')) {
      console.log('🎯 Auto-starting server game after P2P technical failure');
      this.send('start-game', {});
    }
  }

  /**
   * Sign action for P2P consensus
   */
  private async signAction(action: any): Promise<string> {
    const actionData = JSON.stringify(action) + this.playerId;
    const encoder = new TextEncoder();
    const data = encoder.encode(actionData);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate cryptographic nonce
   */
  private generateNonce(): string {
    const nonce = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(nonce).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Check if P2P game mode is enabled
   */
  isP2PGameModeEnabled(): boolean {
    return this.p2pGameMode;
  }

  /**
   * Get current players (helper for P2P initialization)
   */
  private getCurrentPlayers(): any[] {
    // This would come from the current game state
    // For now, return empty array - this should be populated from actual game state
    return [];
  }
  
  /**
   * Convert distributed game state to client format
   */
  private convertDistributedToClientState(distributedState: any): any {
    // Find current player's hole cards from distributed state
    const currentPlayer = this.distributedGameState.getPlayers()
      .find(p => p.id === this.playerId);
    
    return {
      currentPlayerTurn: distributedState.currentPlayerTurn,
      pot: distributedState.pot,
      currentBet: distributedState.currentBet,
      minRaise: distributedState.minRaise,
      dealerPosition: distributedState.dealerPosition,
      smallBlind: distributedState.smallBlind,
      bigBlind: distributedState.bigBlind,
      communityCards: distributedState.communityCards,
      round: distributedState.round,
      bettingRoundComplete: distributedState.bettingRoundComplete,
      playersToAct: distributedState.playersToAct,
      turnStartTime: distributedState.turnStartTime,
      actionTimeoutMs: distributedState.actionTimeoutMs,
      // Player's hole cards
      playerCards: currentPlayer?.holeCards || [],
      // Additional P2P metadata
      stateVersion: distributedState.stateVersion,
      stateHash: distributedState.stateHash,
      isDistributed: true
    };
  }

  /**
   * Get P2P game statistics
   */
  getP2PGameStats(): any {
    if (!this.p2pGameMode) {
      return { enabled: false };
    }

    const distributedState = this.distributedGameState.getGameState();
    const distributedPlayers = this.distributedGameState.getPlayers();

    return {
      enabled: true,
      mode: 'distributed',
      gameEngine: this.p2pGameEngine.getGameStats(),
      byzantineConsensus: this.byzantineConsensus.getConsensusStats(),
      handComparison: this.secureHandComparison.getComparisonStats(),
      distributedState: {
        gameId: distributedState?.gameId,
        stateVersion: distributedState?.stateVersion,
        round: distributedState?.round,
        activePlayers: distributedPlayers.length,
        consensusStatus: 'active'
      }
    };
  }
}

export default HybridCommunicationManager;